import { Fragment, useEffect, useRef, useMemo, useState, useCallback } from 'react'
import * as Sentry from '@sentry/react'
import type {
  EligibilityResult, EligibilityResultWithMembers, Provider, ConfirmedMember,
  CostSharingResolution, CostSharingGroup, PlanMember, EligibilityCheckDoc,
  CorrectionOverride, PaymentOption, PaymentAlert, UserPickOverride,
} from '../types'
import type { VerifyEligibilityResponse } from '../../shared/verifyEligibilityResponse'
import { verifyEligibility } from '../services/eligibilityChecks'
import { extractMedicalSummary } from '../utils/extractMedicalSummary'
import { isDirty } from '../utils/checkCorrection'
import { decideClose, UNSAVED_WARNING } from '../utils/unsavedWork'
import { classifyEligibilityError } from '../utils/classifyError'
import { updatePatient as updatePatientDb } from '../services/patients'
import type { DataService } from '../demo/types'
import { resolveMember, toConfirmedMember } from '../utils/resolveMember'
import type { PatientContext } from '../utils/resolveMember'
import DateInput from './DateInput'
import {
  optionAmountParts, optionCoinsuranceReference, formatPercent, isMultiOptionLayout, hasNoOptions,
  dedupeOptionAlerts, requiresReferral, planCoverageLabel, showsOutOfNetworkFullPrice,
  type OptionAmountPart,
} from '../utils/collectionDisplay'
import { IconShield, IconFile, IconPercent } from './Icons'
import { SlideoutHeaderBar, SlideoutVerificationRecord, SlideoutSummary, SlideoutPayerGrid, SlideoutPayerValue } from './SlideoutHeader'
import RejectionBanner from './RejectionBanner'
import ReferralBanner from './ReferralBanner'
import SmsRecoverySection from './SmsRecoverySection'
import LegacyPatientResponsibility from './LegacyPatientResponsibility'
import RawJsonModal from './RawJsonModal'
import { deidentifyEligibilityExport } from '../utils/deidentify'
import PayerSearch from './PayerSearch'
import type { SelectedPayer } from './PayerSearch'
import { setPayerAlias } from '../services/payerAliases'
import { isMemberIdOptionalForPayer } from '../../shared/stediPayerRegistry'

/** Highlight target passed from slideout → explorer when user clicks "View source rows". */
export interface SourceRowHighlight {
  stc: string
  /** 'A' = coinsurance, 'B' = copay */
  code: 'A' | 'B'
}

interface MedicalSlideoutProps {
  result: EligibilityResult
  confirmedMember: ConfirmedMember
  selectedProvider: Provider
  onClose: () => void
  onViewFullBenefits: () => void
  onViewSourceRows?: (highlight: SourceRowHighlight) => void
  // --- Recovery/rerun props ---
  orgId: string
  /** Working clinic for the re-run + alias write. Null when the org has no
   *  active clinic — the re-run is refused with a readable error then. */
  clinicId: string | null
  providers: Provider[]
  /** Firestore patient ID — used to persist DOB corrections. */
  patientId?: string
  /** Called when a rerun produces a new result so Dashboard can update state in-place. */
  onRerunResult: (result: EligibilityResultWithMembers, provider: Provider, confirmedMember: ConfirmedMember, collection?: EligibilityCheckDoc['collectionResult'] | null, checkId?: string | null, stediPayerId?: string | null, flowId?: string | null) => void
  /** Optional DataService override (demo mode). When provided, bypasses cloud functions + Firestore. */
  dataService?: DataService
  /** AI collection recommendation from the eligibility check doc. */
  collectionResult?: EligibilityCheckDoc['collectionResult'] | null
  /** Pre-V2 collection result — rendered with the legacy card when present
   *  (and `collectionResult` is absent). Mutually exclusive per doc. */
  legacyCollectionResult?: EligibilityCheckDoc['legacyCollectionResult'] | null
  /** Eligibility check doc ID — enables correction feature. */
  checkId?: string
  /** Flow (linear correction chain) of the displayed check. Passed back on a
   *  re-run so the corrected check joins the same chain and the Cockpit row
   *  follows it. Null/absent for legacy docs with no derivable flow. */
  flowId?: string | null
  /** Stable Stedi payer id from the stored check doc (or echoed by a fresh
   *  run). Seeds the edit form so a re-run keeps Original-Medicare
   *  classification without re-picking the payer. Null/absent on legacy docs. */
  storedStediId?: string | null
  /** Active correction override (from Firestore or just submitted). */
  correctionOverride?: CorrectionOverride | null
  /** Triggers correction mode in the BenefitsExplorer. */
  onStartCorrection?: () => void
  /** Reverts a previously submitted correction. */
  onRevertCorrection?: () => void
  /** The payment option the staff member has selected, if any. */
  userPick?: UserPickOverride | null
  /** Persists the staff member's payment-option selection. */
  onPickOption?: (optionId: string) => void
  /** Clears a previously selected payment option. */
  onClearPick?: () => void
  /** Raw payer as received from the appointment source (e.g. Revolution's
   *  "Medicare"). When set and `result.payer.name` differs, the slideout
   *  renders the "Suggested by VitalRev AI" badge next to the payer in the
   *  Payer Details summary. Absent when opened outside an appointment context. */
  originalPayerName?: string
  /** When the check was verified with the payer (ISO 8601). Rendered as the
   *  prominent, timezone-aware date stamp — the load-bearing field for payer
   *  disputes. Absent for a freshly-run check (shown without a stamp). */
  checkedAt?: string
  /** Clinic IANA timezone, so `checkedAt` renders in the clinic's local time
   *  (e.g. "Jun 3, 2026, 2:14 PM EDT"). Falls back to the browser tz if absent. */
  timezone?: string
  /** True when the displayed stored check is in the SMS recovery loop. */
  inRecoveryLoop?: boolean
  /** True when the stored check is SMS-eligible (recoveryAction
   *  patient_correction) — shows the SMS recovery section even before the
   *  outreach sweep claims the check (link disabled until it does). */
  smsRecoveryEligible?: boolean
  /** Jump to this check's Recovery Loop case (the SMS conversation). */
  onViewRecoveryConversation?: () => void
  /** Read-only: no Edit / re-run, no correction, no pick. For a viewer who
   *  cannot act on this check — the staff console's Payer Mappings board
   *  opens a practice's check outside any impersonation session. */
  readOnly?: boolean
}

// ── Helpers ────────────────────────────────────────────────────────────

function formatCurrency(amount: number | null, decimals = 0): string {
  if (amount === null) return '-'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount)
}

function formatDate(dateStr: string): string {
  if (!dateStr || !dateStr.includes('-')) return dateStr || '-'
  const [y, m, d] = dateStr.split('-')
  return `${m}/${d}/${y}`
}

const PLAN_LEVEL_TOOLTIP =
  'This value is from plan-level benefits (STC 30) and may not be specific to this visit type'


function relationshipLabel(rel: string): string {
  switch (rel) {
    case 'self': return 'Subscriber'
    case 'spouse': return 'Spouse'
    case 'child': return 'Child'
    case 'other': return 'Dependent'
    default: return rel
  }
}

function fmtValue(v: number, type: 'copay' | 'coinsurance'): string {
  return type === 'copay' ? formatCurrency(v) : `${v}%`
}

function ResolutionValue({ resolution, type }: { resolution: CostSharingResolution; type: 'copay' | 'coinsurance' }) {
  switch (resolution.mode) {
    case 'single':
      return (
        <span className="slideout-table__value">
          {fmtValue(resolution.value, type)}
          {resolution.planLevel && (
            <span className="slideout-table__plan-badge" title={PLAN_LEVEL_TOOLTIP}> Plan</span>
          )}
        </span>
      )
    case 'range':
      return (
        <span className="slideout-table__value">
          {fmtValue(resolution.min, type)}–{fmtValue(resolution.max, type)}
          {resolution.planLevel && (
            <span className="slideout-table__plan-badge" title={PLAN_LEVEL_TOOLTIP}> Plan</span>
          )}
        </span>
      )
    case 'tiers':
      return (
        <div className="med-tier-list med-tier-list--inline">
          {resolution.tiers.map(t => (
            <div key={t.label} className="med-tier-list__item">
              <span className="med-tier-list__label">{t.label}</span>
              <span className="med-tier-list__value">{fmtValue(t.value, type)}</span>
            </div>
          ))}
          {resolution.planLevel && (
            <span className="slideout-table__plan-badge" title={PLAN_LEVEL_TOOLTIP}>Plan</span>
          )}
        </div>
      )
  }
}

function CostSharingSection({ group, onViewSourceRows }: {
  group: CostSharingGroup
  onViewSourceRows?: (highlight: SourceRowHighlight) => void
}) {
  const label = group.type === 'copay' ? 'Copay' : 'Coinsurance'
  const icon = group.type === 'copay' ? <IconFile /> : <IconPercent />
  const meta = group.type === 'copay' ? 'Office Visit' : 'Member Responsibility'
  const rangeResolution = [group.inNetwork, group.outOfNetwork].find(r => r?.mode === 'range')
  const code = group.type === 'copay' ? 'B' as const : 'A' as const

  return (
    <div className="cost-sharing-card">
      <div className="cost-sharing-card__header">
        <span className="cost-sharing-card__icon">{icon}</span>
        <div>
          <div className="cost-sharing-card__title">{label}</div>
          <div className="cost-sharing-card__meta">{meta}</div>
        </div>
      </div>
      {group.inNetwork && (
        <div className="cost-sharing-card__row">
          <span className="cost-sharing-card__direction">In-Network</span>
          <ResolutionValue resolution={group.inNetwork} type={group.type} />
          {group.inNetwork.mode === 'single' && group.inNetwork.collapsedTierCount && (
            <span className="cost-sharing-card__collapsed-note">
              {group.inNetwork.collapsedTierCount} provider tiers — all at same rate
            </span>
          )}
        </div>
      )}
      {group.outOfNetwork && (
        <div className="cost-sharing-card__row cost-sharing-card__row--oon">
          <span className="cost-sharing-card__direction">Out-of-Network</span>
          <ResolutionValue resolution={group.outOfNetwork} type={group.type} />
          {group.outOfNetwork.mode === 'single' && group.outOfNetwork.collapsedTierCount && (
            <span className="cost-sharing-card__collapsed-note">
              {group.outOfNetwork.collapsedTierCount} provider tiers — all at same rate
            </span>
          )}
        </div>
      )}
      {rangeResolution && (
        <div className="cost-sharing-card__note">
          Provider-dependent
          {onViewSourceRows && (
            <>
              {' · '}
              <button
                className="cost-sharing-card__link"
                onClick={() => onViewSourceRows({ stc: rangeResolution.stc, code })}
              >
                View source rows
              </button>
            </>
          )}
        </div>
      )}
      {(group.inNetwork?.networkUnspecified || group.outOfNetwork?.networkUnspecified) && (
        <div className="cost-sharing-card__note cost-sharing-card__note--muted">
          Network not specified by payer
        </div>
      )}
    </div>
  )
}

function isSimple(r: CostSharingResolution | null | undefined): boolean {
  return !r || r.mode === 'single'
}

function singleVal(r: CostSharingResolution | null | undefined): number | null {
  if (r?.mode === 'single') return r.value
  return null
}

/**
 * A single network row (IN-NETWORK or OUT-OF-NETWORK) within a benefit card.
 * Renders progress bar only when both total and remaining are available.
 */
function BenefitNetworkRow({ direction, total, remaining, showDirection = true }: {
  direction: 'IN-NETWORK' | 'OUT-OF-NETWORK'
  total: number | null
  remaining: number | null
  /** Only show the network label when both directions exist to disambiguate. */
  showDirection?: boolean
}) {
  const hasTotal = total !== null
  const hasRemaining = remaining !== null
  const showBar = hasTotal && hasRemaining && total > 0
  const percent = showBar
    ? Math.min(100, Math.max(0, ((total - remaining) / total) * 100))
    : 0

  return (
    <div className="benefit-progress__row">
      {showDirection && <div className="benefit-progress__direction">{direction}</div>}
      {showBar && (
        <div className="benefit-progress__track">
          <div className="benefit-progress__bar" style={{ width: `${percent}%` }} />
        </div>
      )}
      <div className="benefit-progress__amounts">
        {hasTotal && !hasRemaining && (
          <span className="benefit-progress__total">{formatCurrency(total, 2)}</span>
        )}
        {!hasTotal && hasRemaining && (
          <span className="benefit-progress__remaining">{formatCurrency(remaining, 2)} remaining</span>
        )}
        {hasTotal && hasRemaining && (
          <>
            <span className="benefit-progress__total">{formatCurrency(total, 2)}</span>
            <span className="benefit-progress__remaining">{formatCurrency(remaining, 2)} left</span>
          </>
        )}
      </div>
    </div>
  )
}

/**
 * Benefit-grouped progress card (Deductible or Out-of-Pocket Maximum).
 * Contains up to two network rows; hidden entirely when no data exists.
 */
function BenefitProgressCard({ label, inTotal, inRemaining, oonTotal, oonRemaining }: {
  label: string
  inTotal: number | null
  inRemaining: number | null
  oonTotal: number | null
  oonRemaining: number | null
}) {
  const hasIn = inTotal !== null || inRemaining !== null
  const hasOon = oonTotal !== null || oonRemaining !== null
  if (!hasIn && !hasOon) return null

  // Only show network direction labels when both exist to disambiguate
  const showDirection = hasIn && hasOon

  return (
    <div className="benefit-progress-card">
      <div className="benefit-progress-card__title">{label}</div>
      {hasIn && <BenefitNetworkRow direction="IN-NETWORK" total={inTotal} remaining={inRemaining} showDirection={showDirection} />}
      {hasOon && <BenefitNetworkRow direction="OUT-OF-NETWORK" total={oonTotal} remaining={oonRemaining} showDirection={showDirection} />}
    </div>
  )
}

// ── Inline Member Selector (renders inside slideout when rerun returns multiple members) ──

function InlineMemberSelector({ members, onSelect }: {
  members: PlanMember[]
  onSelect: (member: PlanMember) => void
}) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)

  return (
    <div className="slideout-member-select">
      <p className="slideout-member-select__title">Multiple Members Found</p>
      <p className="slideout-member-select__text">Select the member to view results for:</p>
      <div className="slideout-member-select__list" role="radiogroup">
        {members.map((member, i) => (
          <label
            key={`${member.memberId}-${i}`}
            className={`slideout-member-select__item ${selectedIndex === i ? 'slideout-member-select__item--selected' : ''}`}
          >
            <input
              type="radio"
              name="slideout-member"
              className="slideout-member-select__radio"
              checked={selectedIndex === i}
              onChange={() => setSelectedIndex(i)}
            />
            <div className="slideout-member-select__info">
              <span className="slideout-member-select__name">
                {member.firstName} {member.lastName}
              </span>
              <span className="slideout-member-select__meta">
                {relationshipLabel(member.relationship)}
                {member.dateOfBirth && ` \u00B7 DOB: ${formatDate(member.dateOfBirth)}`}
                {member.memberId && ` \u00B7 ID: ${member.memberId}`}
              </span>
            </div>
          </label>
        ))}
      </div>
      <button
        className="slideout-member-select__confirm"
        disabled={selectedIndex === null}
        onClick={() => selectedIndex !== null && onSelect(members[selectedIndex])}
      >
        Confirm
      </button>
    </div>
  )
}

// ── Patient Responsibility Card ──────────────────────────────────────

type CollectionResultType = NonNullable<EligibilityCheckDoc['collectionResult']>

function formatCorrectedValue(override: CorrectionOverride): string {
  if (override.selectedType === 'copay' && override.selectedAmount !== null) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(override.selectedAmount)
  }
  if (override.selectedType === 'coinsurance' && override.selectedPercent !== null) {
    return `${override.selectedPercent}%`
  }
  return '-'
}

function formatOriginalValue(override: CorrectionOverride): string {
  if (override.originalAmountRange && override.originalAmountRange.length >= 2) {
    return `$${override.originalAmountRange[0]} – $${override.originalAmountRange[override.originalAmountRange.length - 1]}`
  }
  if (override.originalAmount !== null) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(override.originalAmount)
  }
  return '-'
}

// ── Payment-option rendering ─────────────────────────────────────────

/** Shared radio-group name so a pick is exclusive across both networks. */
const PICK_RADIO_NAME = 'pr-option-pick'

/** Drop alerts whose type was already shown — keeps a combined list tidy. */
function dedupeByType(alerts: PaymentAlert[]): PaymentAlert[] {
  const seen = new Set<string>()
  const out: PaymentAlert[] = []
  for (const alert of alerts) {
    if (seen.has(alert.type)) continue
    seen.add(alert.type)
    out.push(alert)
  }
  return out
}

function AlertIcon({ severity }: { severity: PaymentAlert['severity'] }) {
  if (severity === 'info') {
    return (
      <svg className="pr-alert__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4M12 8h.01" />
      </svg>
    )
  }
  // warning + critical share the triangle glyph; severity drives colour via CSS
  return (
    <svg className="pr-alert__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  )
}

/** Renders a list of PaymentAlerts — used at both plan and option scope. */
function AlertList({ alerts }: { alerts: PaymentAlert[] }) {
  if (alerts.length === 0) return null
  return (
    <div className="pr-alerts">
      {alerts.map((alert, i) => (
        <div key={i} className={`pr-alert pr-alert--${alert.severity}`}>
          <AlertIcon severity={alert.severity} />
          <span className="pr-alert__text">{alert.message}</span>
        </div>
      ))}
    </div>
  )
}

function partValue(part: OptionAmountPart): string {
  switch (part.kind) {
    case 'amount': return formatCurrency(part.amount)
    case 'percent': return formatPercent(part.fraction)
    case 'text': return part.text
  }
}

/**
 * Renders an option's collectable value as one or more segments
 * (deductible + copay + coinsurance) joined by "+". Callers pass a non-empty
 * `parts` array — an option with no collectable numbers shows alerts instead.
 */
function OptionAmount({ parts, size }: { parts: OptionAmountPart[]; size: 'hero' | 'inline' }) {
  return (
    <div className={`pr-amount pr-amount--${size}`}>
      {parts.map((part, i) => (
        <Fragment key={i}>
          {i > 0 && <span className="pr-amount__plus">+</span>}
          <span className="pr-amount__seg">
            <span className="pr-amount__value">{partValue(part)}</span>
            <span className="pr-amount__label">{part.label}</span>
          </span>
        </Fragment>
      ))}
    </div>
  )
}

/**
 * Out-of-network "collect full price" value. Rendered (display-only) when the
 * plan distinguishes networks but returned nothing for out-of-network — the
 * plan doesn't cover out-of-network care, so the patient owes the full visit
 * price. Reuses OptionAmount so it sits visually next to real option amounts.
 * See `showsOutOfNetworkFullPrice`.
 */
const FULL_PRICE_PARTS: OptionAmountPart[] = [
  { kind: 'text', text: 'Full price', label: 'no out-of-network coverage' },
]

function FullPriceValue({ size }: { size: 'hero' | 'inline' }) {
  return <OptionAmount parts={FULL_PRICE_PARTS} size={size} />
}

/** Out-of-network section showing the full-price treatment in the case-2 list. */
function FullPriceOonSection() {
  return (
    <div className="pr-network">
      <div className="pr-network__label">Out-of-Network</div>
      <div className="pr-options">
        <div className="pr-option pr-option--static">
          <div className="pr-option__body">
            <div className="pr-option__head">
              <FullPriceValue size="inline" />
              <span className="pr-option__label">Out-of-network not covered</span>
            </div>
            <p className="pr-option__when">
              This plan only covers in-network providers. Collect the full visit price if the provider is out-of-network.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Surfaces inherited plan-level deductible context as meta text for options
 * whose obligationType hides the deductible (copay_only / coinsurance_only).
 * Returns null on a merged option (multiple coverage levels) — the merge
 * step nulls the deductible fields when the bucket inputs disagreed, and
 * keeps them when they agreed; both states are handled by the existing
 * null check.
 */
function inheritedDeductibleMeta(opt: PaymentOption): string | null {
  if (!opt.deductibleInheritedFromStc30) return null
  if (opt.deductibleRemaining == null) return null
  const hidden = opt.obligationType === 'copay_only' || opt.obligationType === 'coinsurance_only'
  if (!hidden) return null
  // No Individual/Family qualifier: the engine collapses IND/FAM into a single
  // option (the minimum remaining binds a single patient), so a coverage-level
  // word here carries no information and can mislead — the number can be the
  // family-aggregate remaining even for one patient.
  return `Plan deductible: ${formatCurrency(opt.deductibleRemaining)} remaining`
}

/** A single payment option — selectable (radio) when `onPick` is supplied. */
function OptionRow({ opt, planAlerts, picked, onPick }: {
  opt: PaymentOption
  planAlerts: PaymentAlert[]
  picked: boolean
  /** Supplied only for applicable options — turns the row into a radio choice. */
  onPick?: () => void
}) {
  const optionAlerts = dedupeOptionAlerts(opt.alerts, planAlerts)
  const parts = optionAmountParts(opt)
  const coinsRef = optionCoinsuranceReference(opt)
  const className = [
    'pr-option',
    onPick ? 'pr-option--selectable' : 'pr-option--static',
    picked ? 'pr-option--picked' : '',
    opt.applicable ? '' : 'pr-option--inapplicable',
  ].filter(Boolean).join(' ')

  const inheritedDedMeta = inheritedDeductibleMeta(opt)

  const body = (
    <div className="pr-option__body">
      <div className="pr-option__head">
        {parts.length > 0
          ? <OptionAmount parts={parts} size="inline" />
          : <span className="pr-option__no-amount">{coinsRef != null ? 'Nothing to collect now' : 'No set amount'}</span>}
        {opt.label && <span className="pr-option__label">{opt.label}</span>}
        {picked && <span className="pr-option__picked-tag">Selected</span>}
      </div>
      {opt.whenToApply && <p className="pr-option__when">{opt.whenToApply}</p>}
      {inheritedDedMeta && <p className="pr-option__meta">{inheritedDedMeta}</p>}
      {coinsRef != null && (
        <p className="pr-option__meta pr-option__meta--coinsurance">
          {formatPercent(coinsRef)} coinsurance — billed after the claim, not collected today
        </p>
      )}
      {!opt.applicable && opt.applicabilityReason && (
        <p className="pr-option__reason">Doesn&rsquo;t apply here — {opt.applicabilityReason}</p>
      )}
      {optionAlerts.length > 0 && <AlertList alerts={optionAlerts} />}
    </div>
  )

  if (onPick) {
    return (
      <label className={className}>
        <input
          type="radio"
          name={PICK_RADIO_NAME}
          className="pr-option__radio"
          checked={picked}
          onChange={onPick}
        />
        {body}
      </label>
    )
  }
  return <div className={className}>{body}</div>
}

/** Compact (case 1) value block for a single option. */
function CompactValue({ opt }: { opt: PaymentOption }) {
  const parts = optionAmountParts(opt)
  const coinsRef = optionCoinsuranceReference(opt)
  if (parts.length === 0 && coinsRef == null) {
    return <span className="pr-option__no-amount">No set amount — see alerts</span>
  }
  return (
    <>
      {parts.length > 0
        ? <OptionAmount parts={parts} size="hero" />
        : <span className="pr-option__no-amount">Nothing to collect now</span>}
      {coinsRef != null && (
        <p className="pr-option__meta pr-option__meta--coinsurance">
          {formatPercent(coinsRef)} coinsurance — billed after the claim, not collected today
        </p>
      )}
    </>
  )
}

/**
 * Case 1 layout — one option per network. Both directions render side by
 * side; a lone direction renders as a single hero value. Option-level alerts
 * from either direction surface below, deduped against the plan-level alerts.
 */
function CompactOptions({ inOption, oonOption, fullPriceOon, inLabel, planAlerts, statusText }: {
  inOption: PaymentOption | null
  oonOption: PaymentOption | null
  /** Show the OON column as "Full price" when the plan doesn't cover out-of-network. */
  fullPriceOon: boolean
  inLabel: string
  planAlerts: PaymentAlert[]
  statusText: string
}) {
  const optionAlerts = dedupeByType(
    dedupeOptionAlerts(
      [...(inOption?.alerts ?? []), ...(oonOption?.alerts ?? [])],
      planAlerts,
    ),
  )

  // OON column: a real option when present, else the synthetic full-price
  // treatment when the plan distinguishes networks but specified no OON.
  const oonContent = oonOption
    ? <CompactValue opt={oonOption} />
    : fullPriceOon ? <FullPriceValue size="hero" /> : null

  if (inOption && oonContent) {
    return (
      <>
        <div className="patient-responsibility__dual">
          <div className="patient-responsibility__dual-col">
            <span className="patient-responsibility__dual-label">{inLabel}</span>
            <CompactValue opt={inOption} />
          </div>
          <div className="patient-responsibility__dual-divider" />
          <div className="patient-responsibility__dual-col">
            <span className="patient-responsibility__dual-label">Out-of-Network</span>
            {oonContent}
          </div>
        </div>
        {optionAlerts.length > 0 && <AlertList alerts={optionAlerts} />}
        {statusText && <p className="patient-responsibility__context">{statusText}</p>}
      </>
    )
  }

  const single = inOption ?? oonOption
  if (!single) return null
  return (
    <>
      <div className="pr-compact-single">
        <span className="pr-compact-single__label">{inOption ? inLabel : 'Out-of-Network'}</span>
        <CompactValue opt={single} />
      </div>
      {optionAlerts.length > 0 && <AlertList alerts={optionAlerts} />}
      {statusText && <p className="patient-responsibility__context">{statusText}</p>}
    </>
  )
}

/** One network direction in the case-2 list — applicable rows only.
 *  Not-applicable options are intentionally omitted from the collection panel
 *  to avoid confusing front-desk staff; the full set lives in the Benefits
 *  Explorer, where staff can pick a different cost-sharing line if needed. */
function NetworkSection({ label, options, planAlerts, pickedId, onPick }: {
  label: string
  options: PaymentOption[]
  planAlerts: PaymentAlert[]
  pickedId: string | null
  onPick?: (optionId: string) => void
}) {
  const applicable = options.filter(o => o.applicable)
  return (
    <div className="pr-network">
      <div className="pr-network__label">{label}</div>
      {applicable.length > 0 ? (
        <div className="pr-options">
          {applicable.map(opt => (
            <OptionRow
              key={opt.id}
              opt={opt}
              planAlerts={planAlerts}
              picked={pickedId === opt.id}
              onPick={onPick ? () => onPick(opt.id) : undefined}
            />
          ))}
        </div>
      ) : (
        <p className="pr-network__none">No standard option applies to this visit — review the full benefits to choose one.</p>
      )}
    </div>
  )
}

function PatientResponsibility({
  cr, override, onStartCorrection, onRevertCorrection, canCorrect,
  userPick, onPickOption, onClearPick,
}: {
  cr: CollectionResultType
  override?: CorrectionOverride | null
  onStartCorrection?: () => void
  onRevertCorrection?: () => void
  canCorrect: boolean
  userPick?: UserPickOverride | null
  onPickOption?: (optionId: string) => void
  onClearPick?: () => void
}) {
  if (override) {
    const typeLabel = override.selectedType === 'copay' ? 'Co-payment' : 'Co-insurance'
    const networkLabel = override.selectedNetwork === 'in-network' ? 'In-Network' : 'Out-of-Network'
    return (
      <div className="patient-responsibility patient-responsibility--overridden">
        <div className="patient-responsibility__header">PATIENT RESPONSIBILITY</div>
        <div className="patient-responsibility__amount-row">
          <span className="patient-responsibility__amount">{formatCorrectedValue(override)}</span>
          <span className="patient-responsibility__type">{typeLabel} · {networkLabel}</span>
        </div>
        <table className="patient-responsibility__override-table">
          <tbody>
            <tr>
              <td className="patient-responsibility__override-label">Source</td>
              <td>STC {override.selectedStc} {override.selectedStcLabel}</td>
            </tr>
            <tr>
              <td className="patient-responsibility__override-label">Original AI value</td>
              <td><s>{formatOriginalValue(override)}</s></td>
            </tr>
            <tr>
              <td className="patient-responsibility__override-label">Reason</td>
              <td>{override.explanation}</td>
            </tr>
          </tbody>
        </table>
        <div className="patient-responsibility__override-footer">
          {onRevertCorrection && (
            <button className="patient-responsibility__revert" onClick={onRevertCorrection}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path d="M3 3v5h5" />
              </svg>
              Revert to AI suggestion
            </button>
          )}
          <span className="patient-responsibility__override-badge">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
            </svg>
            Manually overridden
          </span>
        </div>
      </div>
    )
  }

  const planAlerts = cr.alerts ?? []
  const inOptions = cr.inNetwork?.paymentOptions ?? []
  const oonOptions = cr.outOfNetwork?.paymentOptions ?? []
  const inLabel = cr.networkStatus === 'unspecified' ? 'Network Not Specified' : 'In-Network'
  const pickedId = userPick?.optionId ?? null
  const statusText = cr.statusText ?? ''
  // Plan distinguishes networks but returned nothing for OON → out-of-network
  // isn't covered, so the patient owes the full price out of network.
  const fullPriceOon = showsOutOfNetworkFullPrice(cr)

  // A radio pick only makes sense when there's more than one applicable option
  // to choose between. The multi-option layout can fire (a network returned >1
  // raw option) yet leave a single *applicable* option once not-applicable rows
  // are filtered out — in that case render it statically, like the compact
  // single-option layout, instead of a lone radio with nothing to pick against.
  const selectableOptionCount =
    inOptions.filter(o => o.applicable).length +
    oonOptions.filter(o => o.applicable).length
  const pickHandler = selectableOptionCount > 1 ? onPickOption : undefined

  return (
    <div className="patient-responsibility">
      <div className="patient-responsibility__header">
        PATIENT RESPONSIBILITY
      </div>

      {planAlerts.length > 0 && <AlertList alerts={planAlerts} />}

      {hasNoOptions(cr) ? (
        <div className="patient-responsibility__empty">
          <p className="patient-responsibility__empty-title">Unable to determine patient responsibility</p>
          <p className="patient-responsibility__empty-text">
            The payer&rsquo;s response didn&rsquo;t include cost-sharing for this visit type. Verify with the payer before collecting.
          </p>
        </div>
      ) : isMultiOptionLayout(cr) ? (
        <>
          <div className="pr-networks">
            {inOptions.length > 0 && (
              <NetworkSection
                label={inLabel}
                options={inOptions}
                planAlerts={planAlerts}
                pickedId={pickedId}
                onPick={pickHandler}
              />
            )}
            {oonOptions.length > 0 && (
              <NetworkSection
                label="Out-of-Network"
                options={oonOptions}
                planAlerts={planAlerts}
                pickedId={pickedId}
                onPick={pickHandler}
              />
            )}
            {fullPriceOon && <FullPriceOonSection />}
          </div>
          {pickHandler && pickedId && onClearPick && (
            <button type="button" className="pr-clear-pick" onClick={onClearPick}>
              Clear selection
            </button>
          )}
          {statusText && <p className="patient-responsibility__context">{statusText}</p>}
        </>
      ) : (
        <CompactOptions
          inOption={inOptions[0] ?? null}
          oonOption={oonOptions[0] ?? null}
          fullPriceOon={fullPriceOon}
          inLabel={inLabel}
          planAlerts={planAlerts}
          statusText={statusText}
        />
      )}


      {canCorrect && (
        <button type="button" className="patient-responsibility__flag-cta" onClick={onStartCorrection}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
            <line x1="4" y1="22" x2="4" y2="15" />
          </svg>
          <span>Flag as incorrect</span>
        </button>
      )}

      <div className="patient-responsibility__badge">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
        </svg>
        Generated by VitalRev AI
      </div>
    </div>
  )
}

// ── AI Thought Process (expandable) ──────────────────────────────────

function AiThoughtProcess({ reasoning }: { reasoning: string }) {
  const [open, setOpen] = useState(false)
  const steps = reasoning
    .split(/(?:\.\s+|\n)/)
    .map(s => s.trim())
    .filter(s => s.length > 0)

  if (steps.length === 0) return null

  return (
    <div className="ai-thought">
      <button className="ai-thought__toggle" onClick={() => setOpen(prev => !prev)}>
        <svg className="ai-thought__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
        </svg>
        <span>AI Thought Process</span>
        <svg className={`ai-thought__chevron ${open ? 'ai-thought__chevron--open' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="ai-thought__content">
          <ol className="ai-thought__steps">
            {steps.map((step, i) => (
              <li key={i} className="ai-thought__step">{step}</li>
            ))}
          </ol>
        </div>
      )}
    </div>
  )
}

// ── Deductible & OOP Accordion ───────────────────────────────────────

function DeductibleAccordion({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false)

  return (
    <div className="deductible-accordion">
      <button className="deductible-accordion__toggle" onClick={() => setOpen(prev => !prev)}>
        <svg className="deductible-accordion__icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" />
          <path d="M12 18V6" />
        </svg>
        <span>Deductible &amp; OOP Details</span>
        <svg className={`deductible-accordion__chevron ${open ? 'deductible-accordion__chevron--open' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="deductible-accordion__content">
          {children}
        </div>
      )}
    </div>
  )
}

// ── Main Component ────────────────────────────────────────────────────

export default function MedicalSlideout({
  result,
  confirmedMember,
  selectedProvider,
  onClose,
  onViewFullBenefits,
  onViewSourceRows,
  orgId,
  clinicId,
  providers,
  patientId,
  onRerunResult,
  dataService,
  collectionResult,
  legacyCollectionResult,
  checkId,
  flowId,
  storedStediId,
  correctionOverride,
  onStartCorrection,
  onRevertCorrection,
  userPick,
  onPickOption,
  onClearPick,
  originalPayerName,
  checkedAt,
  timezone,
  inRecoveryLoop,
  smsRecoveryEligible,
  onViewRecoveryConversation,
  readOnly = false,
}: MedicalSlideoutProps) {
  const summary = useMemo(() => extractMedicalSummary(result), [result])
  const panelRef = useRef<HTMLDivElement>(null)
  const [showRawJson, setShowRawJson] = useState(false)
  const [showCorrectionToast, setShowCorrectionToast] = useState(false)
  // JSON inspector/export shows in non-prod builds (deployed dev/staging
  // included). `import.meta.env.DEV` was serve-only, so built bundles hid it.
  const jsonExportEnabled = import.meta.env.MODE !== 'production'
  // Full check payload for the JSON inspector/download — the 271 plus whichever
  // collection recommendation is present (V2/new-shape or legacy). De-identified
  // (names / member IDs / DOBs stripped) so exports are safe to commit as test
  // fixtures — only the Stedi benefits/plan data remains.
  const jsonExport = deidentifyEligibilityExport({
    result,
    ...(collectionResult ? { collectionResult } : {}),
    ...(legacyCollectionResult ? { legacyCollectionResult } : {}),
  })

  // Show toast when a correction override appears (user just submitted one)
  const prevOverrideRef = useRef(correctionOverride)
  useEffect(() => {
    if (correctionOverride && !prevOverrideRef.current) {
      setShowCorrectionToast(true)
      const timer = setTimeout(() => setShowCorrectionToast(false), 3000)
      return () => clearTimeout(timer)
    }
    prevOverrideRef.current = correctionOverride
  }, [correctionOverride])

  // #314: correction is offered on EVERY settled check, active coverage
  // included. A check that came back green against the wrong payer is
  // indistinguishable on screen from a correct one — nobody investigates a
  // green row — and the alias learner may already have written that wrong
  // payer as the clinic's default for the shorthand. Gating the Edit button on
  // `status !== 'active'` left exactly that case with no way to fix it.
  const isRecoverable = true

  // ── Edit mode state ──────────────────────────────────────────────
  const [isEditing, setIsEditing] = useState(false)
  const [rerunning, setRerunning] = useState(false)
  const [rerunError, setRerunError] = useState<string | null>(null)
  // The clinic-wide mapping offer (#314 review) — set after a corrected re-run
  // the payer answered, never written without an explicit click.
  const [aliasOffer, setAliasOffer] = useState<{
    shorthand: string
    tradingPartnerServiceId: string
    stediPayerId: string | null
    resolvedName: string
  } | null>(null)
  const [aliasNotice, setAliasNotice] = useState<string | null>(null)
  const [pendingMembers, setPendingMembers] = useState<PlanMember[] | null>(null)
  const [pendingResult, setPendingResult] = useState<EligibilityResultWithMembers | null>(null)
  const [pendingCollection, setPendingCollection] = useState<EligibilityCheckDoc['collectionResult'] | null>(null)
  const [pendingCheckId, setPendingCheckId] = useState<string | null>(null)
  const [pendingStediId, setPendingStediId] = useState<string | null>(null)
  const [pendingFlowId, setPendingFlowId] = useState<string | null>(null)

  // Editable fields — initialized from current result/member/provider.
  // stediId comes from the stored check doc (storedStediId) so a re-run keeps
  // Original-Medicare classification; null on legacy docs written before it
  // was persisted. Picking a payer from the dropdown replaces it with
  // PayerSearch's response.
  const [editPayer, setEditPayer] = useState<SelectedPayer>({
    name: result.payer.name,
    tradingPartnerServiceId: result.payer.payerId,
    stediId: storedStediId ?? null,
  })
  // Legacy checks (written before stediPayerId was persisted) carry no stable
  // payer identity, so a re-run would skip payer-specific routing (Original
  // Medicare's group-NPI/MBI rails). Block re-run until the user re-picks the
  // payer from the dropdown, which restores the stediId. A fresh pick clears
  // the flag even if the directory has no stediId for that payer — that null
  // is ground truth, not a storage gap.
  const [payerNeedsRepick, setPayerNeedsRepick] = useState(!storedStediId)
  const [editProviderId, setEditProviderId] = useState(selectedProvider.id)
  const [editMemberId, setEditMemberId] = useState(confirmedMember.memberId)
  // #314: the payer is asked about the NAME too — a check can be wrong because
  // the name it carried was wrong, and until now that was the one identity
  // field this panel could not fix.
  const [editFirstName, setEditFirstName] = useState(confirmedMember.firstName)
  const [editLastName, setEditLastName] = useState(confirmedMember.lastName)
  const [editDob, setEditDob] = useState(confirmedMember.dateOfBirth)

  // Reset edit fields when result changes (after successful rerun)
  useEffect(() => {
    setEditPayer({
      name: result.payer.name,
      tradingPartnerServiceId: result.payer.payerId,
      stediId: storedStediId ?? null,
    })
    setPayerNeedsRepick(!storedStediId)
    setEditMemberId(confirmedMember.memberId)
    setEditFirstName(confirmedMember.firstName)
    setEditLastName(confirmedMember.lastName)
    setEditDob(confirmedMember.dateOfBirth)
    setEditProviderId(selectedProvider.id)
    setIsEditing(false)
    setRerunError(null)
    setPendingMembers(null)
    setPendingResult(null)
  }, [result, confirmedMember, selectedProvider, storedStediId])

  const editProvider = providers.find(p => p.id === editProviderId) ?? selectedProvider

  // #274: what closing would discard. Field comparison reuses the shared rules
  // (a member id retyped with different separators is not an edit), plus the
  // provider, which only this panel can change.
  const editDirty = isEditing && (
    isDirty(
      {
        firstName: confirmedMember.firstName,
        lastName: confirmedMember.lastName,
        dateOfBirth: confirmedMember.dateOfBirth,
        memberId: confirmedMember.memberId,
        payerName: originalPayerName ?? result.payer?.name ?? '',
      },
      {
        firstName: editFirstName,
        lastName: editLastName,
        dateOfBirth: editDob,
        memberId: editMemberId,
        payerName: editPayer.name,
      },
    ) || editProviderId !== selectedProvider.id
  )
  const [closeWarned, setCloseWarned] = useState(false)
  useEffect(() => { if (!editDirty) setCloseWarned(false) }, [editDirty])

  /** Every close path goes through here — Escape, the X, and the overlay — so
   *  none of them can be the one that loses the operator's work. */
  const guardedClose = useCallback(() => {
    if (decideClose(editDirty, closeWarned) === 'confirm') {
      setCloseWarned(true)
      return
    }
    onClose()
  }, [editDirty, closeWarned, onClose])
  const editMemberIdOptional = isMemberIdOptionalForPayer(editPayer.stediId)
  const canRerun = !!editPayer.tradingPartnerServiceId
    && !payerNeedsRepick
    && (editMemberIdOptional || !!editMemberId)

  // ── Rerun handler ────────────────────────────────────────────────

  const handleRerun = useCallback(async () => {
    if (!canRerun || rerunning) return
    // Clinic-scoped callable: refuse a null working clinic (org has no
    // active clinic) with a readable error instead of a server bounce.
    if (!clinicId) {
      setRerunError('No active clinic to run the re-check under. Create or unarchive a clinic first.')
      return
    }

    setRerunning(true)
    setRerunError(null)
    setPendingMembers(null)
    setPendingResult(null)

    // If DOB changed and a patient record exists, update it first
    const dobChanged = editDob !== confirmedMember.dateOfBirth
    if (dobChanged && patientId) {
      try {
        const updateFn = dataService ? dataService.updatePatient : updatePatientDb
        await updateFn(orgId, patientId, { dateOfBirth: editDob })
      } catch (err) {
        if (!dataService) Sentry.captureException(err)
        // Non-blocking: log but continue with rerun
      }
    }

    try {
      const rerunInput = {
        orgId,
        clinicId,
        providerId: editProviderId,
        subscriber: {
          firstName: editFirstName,
          lastName: editLastName,
          memberId: editMemberId,
          dateOfBirth: editDob,
        },
        tradingPartnerServiceId: editPayer.tradingPartnerServiceId,
        stediPayerId: editPayer.stediId,
        payerName: editPayer.name,
        // The corrected check continues the displayed check's flow — the
        // Cockpit row renders the newest check of this chain — and inherits
        // the displayed check's patient contact + SMS candidacy server-side.
        flowId: flowId ?? undefined,
        sourceCheckId: checkId ?? undefined,
      }

      let rerunResponse: VerifyEligibilityResponse
      if (dataService) {
        rerunResponse = await dataService.verifyEligibility(rerunInput)
      } else {
        rerunResponse = await verifyEligibility(rerunInput)
      }

      // Slideout rerun is Medical-only by construction — if the server routes
      // to an RPA bot (shouldn't normally happen), bail out with a readable error.
      if (rerunResponse.kind !== 'stedi_resolved') {
        setRerunError(
          `The selected payer routes to the ${rerunResponse.rpaVendor.toUpperCase()} bot. Please submit this via the main form and track status in Recent Checks.`,
        )
        return
      }
      const newResult: EligibilityResultWithMembers = rerunResponse.data
      const newCollection: EligibilityCheckDoc['collectionResult'] | null = rerunResponse.collection ?? null
      const newCheckId: string | null = rerunResponse.checkId ?? null
      // Prefer the server's echo (authoritative for what the check ran
      // against); fall back to what we submitted (demo service doesn't echo).
      const newStediId: string | null = rerunResponse.stediPayerId ?? editPayer.stediId
      const newFlowId: string | null = rerunResponse.flowId ?? flowId ?? null

      // A check-level fix must never silently become a clinic-wide rule. This
      // panel used to fire setPayerAlias on every corrected re-run with no
      // offer and no confirmation — and #314 opened it for GREEN checks, where
      // a wrong mapping is hardest to notice and where the alias learner may
      // already have cemented the wrong payer for this shorthand. It is now the
      // same explicit, separate confirmation the failure-panel editor makes.
      //
      // Offered only when the payer actually ANSWERED (active/inactive — the
      // learn-on-success bar): an `unknown` or a lookup_error is a data
      // problem, not evidence about the mapping. Rerun is Medical-only by
      // construction (guarded above), so the route is always Stedi.
      if (
        originalPayerName?.trim() && editPayer.tradingPartnerServiceId &&
        (newResult.status === 'active' || newResult.status === 'inactive') &&
        editPayer.name.trim().toLowerCase() !== originalPayerName.trim().toLowerCase()
      ) {
        setAliasOffer({
          shorthand: originalPayerName.trim(),
          tradingPartnerServiceId: editPayer.tradingPartnerServiceId,
          stediPayerId: editPayer.stediId,
          resolvedName: editPayer.name,
        })
      }
      const members = newResult.members ?? [{
        firstName: newResult.subscriber.firstName,
        lastName: newResult.subscriber.lastName,
        memberId: newResult.subscriber.memberId,
        dateOfBirth: newResult.subscriber.dateOfBirth,
        relationship: 'self' as const,
        relationshipCode: '18',
      }]
      const resultWithMembers = { ...newResult, members }

      // Resolve member from the new response
      const patientContext: PatientContext = {
        firstName: confirmedMember.firstName,
        lastName: confirmedMember.lastName,
        dateOfBirth: editDob,
        memberId: editMemberId,
      }

      const isSparse = members.length === 0
        || (members.every(m => !m.firstName && !m.lastName) && newResult.status === 'unknown')

      if (isSparse) {
        const cm: ConfirmedMember = {
          firstName: editFirstName,
          lastName: editLastName,
          memberId: editMemberId,
          dateOfBirth: editDob,
          relationship: 'self',
          isDependent: false,
        }
        onRerunResult(resultWithMembers, editProvider, cm, newCollection, newCheckId, newStediId, newFlowId)
      } else {
        const resolution = resolveMember(members, patientContext)
        if (resolution.type === 'exact') {
          onRerunResult(resultWithMembers, editProvider, toConfirmedMember(resolution.member), newCollection, newCheckId, newStediId, newFlowId)
        } else {
          // Multiple members — show inline selector
          setPendingResult(resultWithMembers)
          setPendingMembers(members)
          setPendingCollection(newCollection)
          setPendingCheckId(newCheckId)
          setPendingStediId(newStediId)
          setPendingFlowId(newFlowId)
        }
      }
    } catch (err) {
      if (!dataService) Sentry.captureException(err)
      const classified = classifyEligibilityError(err)
      setRerunError(classified.title + '. ' + classified.guidance)
    } finally {
      setRerunning(false)
    }
  }, [
    canRerun, rerunning, editDob, editMemberId, editFirstName, editLastName,
    editPayer, editProviderId, editProvider, confirmedMember, orgId, clinicId, patientId, onRerunResult, dataService, originalPayerName, flowId, checkId,
  ])

  function handleMemberSelected(member: PlanMember) {
    if (pendingResult) {
      onRerunResult(pendingResult, editProvider, toConfirmedMember(member), pendingCollection, pendingCheckId, pendingStediId, pendingFlowId)
    }
    setPendingMembers(null)
    setPendingResult(null)
  }

  // ── Scroll lock + initial focus (mount-only) ────────────────────

  useEffect(() => {
    const original = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    panelRef.current?.focus()
    return () => { document.body.style.overflow = original }
  }, [])

  // ── Keyboard handler (uses refs to avoid re-registering on every keystroke) ──

  const handleRerunRef = useRef(handleRerun)
  const guardedCloseRef = useRef(guardedClose)
  useEffect(() => { guardedCloseRef.current = guardedClose }, [guardedClose])
  const stateRef = useRef({ isEditing, canRerun, rerunning })
  useEffect(() => { handleRerunRef.current = handleRerun }, [handleRerun])
  useEffect(() => { stateRef.current = { isEditing, canRerun, rerunning } }, [isEditing, canRerun, rerunning])

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') guardedCloseRef.current()
      const { isEditing: ed, canRerun: cr, rerunning: rr } = stateRef.current
      if (e.key === 'Enter' && ed && cr && !rr) {
        const tag = (e.target as HTMLElement)?.tagName
        if (tag !== 'SELECT' && tag !== 'BUTTON') {
          e.preventDefault()
          handleRerunRef.current()
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // ── Derived layout values ────────────────────────────────────────

  const hasDeductible =
    summary.deductibleTotal !== null || summary.deductibleRemaining !== null ||
    summary.deductibleOonTotal !== null || summary.deductibleOonRemaining !== null
  const hasOop =
    summary.oopTotal !== null || summary.oopRemaining !== null ||
    summary.oopOonTotal !== null || summary.oopOonRemaining !== null

  const useCompactTable =
    isSimple(summary.copay?.inNetwork) &&
    isSimple(summary.copay?.outOfNetwork) &&
    isSimple(summary.coinsurance?.inNetwork) &&
    isSimple(summary.coinsurance?.outOfNetwork)

  const hasCostSharing = summary.copay || summary.coinsurance

  // LLM reasoning across both network directions — feeds the expandable trace.
  const llmReasoning = (collectionResult
    ? [
        ...(collectionResult.inNetwork?.paymentOptions ?? []),
        ...(collectionResult.outOfNetwork?.paymentOptions ?? []),
      ]
    : []
  ).filter(o => o.source.startsWith('llm_')).map(o => o.reasoning)

  // ── Render ───────────────────────────────────────────────────────

  return (
    <>
      <div className="slideout-overlay" onClick={guardedClose} />

      <div
        className="slideout-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Medical Summary"
        ref={panelRef}
        tabIndex={-1}
      >
        {/* Header */}
        <SlideoutHeaderBar
          icon={<IconShield />}
          title="Medical Summary"
          subtitle="Eligibility & benefit overview"
          onClose={guardedClose}
          actions={
            <>
              {jsonExportEnabled && (
                <button
                  className="slideout-panel__json-btn"
                  onClick={() => setShowRawJson(true)}
                  title="Show / download JSON"
                >
                  {'{ }'}
                </button>
              )}
              {!readOnly && isRecoverable && !isEditing && (
                <button className="slideout-edit-btn" onClick={() => setIsEditing(true)}>
                  Edit
                </button>
              )}
              {isEditing && (
                <button
                  className="slideout-edit__cancel"
                  onClick={() => {
                    setIsEditing(false)
                    setRerunError(null)
                    setEditPayer({ name: result.payer.name, tradingPartnerServiceId: result.payer.payerId, stediId: storedStediId ?? null })
                    setPayerNeedsRepick(!storedStediId)
                    setEditProviderId(selectedProvider.id)
                    setEditMemberId(confirmedMember.memberId)
                    setEditDob(confirmedMember.dateOfBirth)
                  }}
                >
                  Cancel
                </button>
              )}
            </>
          }
        />

        {/* Verification record: prominent date stamp (the payer-dispute proof)
            + one-click permalink to file in the patient chart. */}
        <SlideoutVerificationRecord checkedAt={checkedAt} timezone={timezone} checkId={checkId} />

        {/* Content */}
        <div className="slideout-panel__content">
          {/* Top: Patient / Provider + Payer Details. The read-only view renders
              through the shared SlideoutSummary / SlideoutPayerGrid (identical to
              the vision slideouts); edit mode swaps in the inline editable form. */}
          {isEditing ? (
            <div className="med-summary-split">
              <div className="med-summary-split__col">
                <span className="med-label">COVERAGE VERIFIED FOR</span>
                <div className="med-value-large">
                  {confirmedMember.firstName} {confirmedMember.lastName}
                  {confirmedMember.isDependent && (
                    <span className="member-identity-banner__dep-tag">Dependent</span>
                  )}
                </div>
                <div className="slideout-edit__fields">
                  <div className="form-field">
                    <label className="form-field__label" htmlFor="slideout-dob">Date of Birth</label>
                    <DateInput
                      id="slideout-dob"
                      className="form-field__input"
                      value={editDob}
                      onValueChange={setEditDob}
                    />
                  </div>
                </div>
              </div>
              <div className="med-summary-split__divider" />
              <div className="med-summary-split__col">
                <span className="med-label">PROVIDER</span>
                <div className="slideout-edit__fields">
                  <div className="form-field">
                    <label className="form-field__label" htmlFor="slideout-provider">Doctor</label>
                    <select
                      id="slideout-provider"
                      className="form-field__select"
                      value={editProviderId}
                      onChange={e => setEditProviderId(e.target.value)}
                    >
                      {providers.map(p => (
                        <option key={p.id} value={p.id}>
                          Dr. {p.firstName} {p.lastName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="med-meta">NPI: {editProvider.npi || '—'}</div>
                </div>
              </div>
            </div>
          ) : (
            <SlideoutSummary
              patientName={<>{confirmedMember.firstName} {confirmedMember.lastName}</>}
              isDependent={confirmedMember.isDependent}
              dob={confirmedMember.dateOfBirth}
              relationship={relationshipLabel(confirmedMember.relationship)}
              providerName={<>{selectedProvider.firstName} {selectedProvider.lastName}</>}
              providerNpi={selectedProvider.npi}
            />
          )}

          <div className="med-divider" />

          {/* Payer Details — editable in edit mode */}
          {isEditing ? (
            <div className="med-summary-section">
              <div className="med-label-title">PAYER DETAILS</div>
              <div className="slideout-edit__fields">
                <PayerSearch
                  value={editPayer.name}
                  onSelect={(p: SelectedPayer) => { setEditPayer(p); setPayerNeedsRepick(false) }}
                  onClear={() => setEditPayer({ name: '', tradingPartnerServiceId: '', stediId: null })}
                  inputId="slideout-payer-search"
                  searchFn={dataService?.searchPayers}
                  invalidMessage={payerNeedsRepick
                    ? 'Please re-select the payer from the list.'
                    : null}
                />
                <div className="form-field">
                  <label className="form-field__label" htmlFor="slideout-member-id">
                    Member ID{editMemberIdOptional && <span className="form-field__optional"> (optional)</span>}
                  </label>
                  <input
                    id="slideout-member-id"
                    className="form-field__input"
                    value={editMemberId}
                    onChange={e => setEditMemberId(e.target.value)}
                    placeholder="ABC123456"
                  />
                </div>
                <div className="form-field">
                  <label className="form-field__label" htmlFor="slideout-first-name">
                    First name
                  </label>
                  <input
                    id="slideout-first-name"
                    className="form-field__input"
                    value={editFirstName}
                    onChange={e => setEditFirstName(e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label className="form-field__label" htmlFor="slideout-last-name">
                    Last name
                  </label>
                  <input
                    id="slideout-last-name"
                    className="form-field__input"
                    value={editLastName}
                    onChange={e => setEditLastName(e.target.value)}
                  />
                </div>
              </div>
              {rerunError && (
                <div className="slideout-edit__error">
                  <p>{rerunError}</p>
                </div>
              )}
            </div>
          ) : (
            <SlideoutPayerGrid
              payer={<SlideoutPayerValue name={result.payer.name} resolvedFrom={originalPayerName} />}
              plan={result.planName}
              memberId={confirmedMember.memberId}
              group={result.groupNumber}
              coverage={planCoverageLabel(result.planCoverageLevel)}
            />
          )}

          {aliasOffer && clinicId && (
            <div className="check-edit__mapping" role="status">
              <p className="check-edit__mapping-text">
                This clinic&apos;s <strong>{aliasOffer.shorthand}</strong> still resolves
                to the old payer. Map it to <strong>{aliasOffer.resolvedName}</strong> for
                every future check?
              </p>
              <div className="check-edit__mapping-actions">
                <button
                  type="button"
                  className="check-edit__mapping-yes"
                  onClick={async () => {
                    const offer = aliasOffer
                    setAliasOffer(null)
                    try {
                      await setPayerAlias({
                        clinicId,
                        shorthand: offer.shorthand,
                        route: 'stedi',
                        tradingPartnerServiceId: offer.tradingPartnerServiceId,
                        stediPayerId: offer.stediPayerId,
                        resolvedName: offer.resolvedName,
                      })
                      setAliasNotice(`"${offer.shorthand}" now resolves to ${offer.resolvedName}.`)
                    } catch {
                      setAliasNotice('This one check was fixed, but the clinic mapping could not be saved.')
                    }
                  }}
                >
                  Map it for this clinic
                </button>
                <button
                  type="button"
                  className="check-edit__mapping-no"
                  onClick={() => setAliasOffer(null)}
                >
                  Just this check
                </button>
              </div>
            </div>
          )}
          {aliasNotice && <p className="check-edit__notice">{aliasNotice}</p>}

          {/* Referral-required warning — shown when the 271 flags a PCP referral requirement.
              Follows the responsibility card's winner: legacy alerts are only
              consulted when there is no V2 result (see branch order below). */}
          {(collectionResult
            ? requiresReferral(collectionResult)
            : (legacyCollectionResult?.alerts?.some(a => a.type === 'referral_required') ?? false))
            && <ReferralBanner />}

          {/* Inline member selector (when rerun returns multiple members) */}
          {pendingMembers && (
            <InlineMemberSelector members={pendingMembers} onSelect={handleMemberSelected} />
          )}

          {/* Status-specific content */}
          {result.status === 'inactive' ? (
            <div className="slideout-panel__inactive">
              <p className="slideout-panel__inactive-title">Coverage Terminated</p>
              <p className="slideout-panel__inactive-text">
                {result.terminationDate || result.effectiveDate
                  ? <>Coverage terminated {formatDate(result.terminationDate ?? result.effectiveDate)}.</>
                  : <>Coverage is no longer active.</>
                }{' '}
                Verify with patient or payer.
              </p>
            </div>
          ) : result.status === 'lookup_error' ? (
            <RejectionBanner result={result} />
          ) : result.status === 'unknown' ? (
            <div className="slideout-panel__no-data">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <p className="slideout-panel__no-data-title">No Benefits Data Returned</p>
              <p className="slideout-panel__no-data-text">
                Payer did not return benefit details. Verify member ID and demographics.
              </p>
            </div>
          ) : collectionResult ? (
            // V2 outranks legacy by design: per stored check the two are
            // mutually exclusive (normalizeLegacy), so both being set means
            // one is a stale leftover — and only legacyCollectionResult can
            // be stale (every fresh result is V2). Render the current check.
            <>
              <PatientResponsibility
                cr={collectionResult}
                override={correctionOverride}
                onStartCorrection={onStartCorrection}
                onRevertCorrection={onRevertCorrection}
                canCorrect={!readOnly && !!checkId && !correctionOverride}
                userPick={userPick}
                onPickOption={onPickOption}
                onClearPick={onClearPick}
              />

              {llmReasoning.length > 0 && (
                <AiThoughtProcess reasoning={llmReasoning.join('; ')} />
              )}

              {(hasDeductible || hasOop) && (
                <DeductibleAccordion>
                  <div className="benefit-progress-cards">
                    {hasDeductible && (
                      <BenefitProgressCard
                        label="Deductible"
                        inTotal={summary.deductibleTotal}
                        inRemaining={summary.deductibleRemaining}
                        oonTotal={summary.deductibleOonTotal}
                        oonRemaining={summary.deductibleOonRemaining}
                      />
                    )}
                    {hasOop && (
                      <BenefitProgressCard
                        label="Out-of-Pocket Maximum"
                        inTotal={summary.oopTotal}
                        inRemaining={summary.oopRemaining}
                        oonTotal={summary.oopOonTotal}
                        oonRemaining={summary.oopOonRemaining}
                      />
                    )}
                  </div>
                </DeductibleAccordion>
              )}
            </>
          ) : legacyCollectionResult ? (
            <>
              {/* New corrections are disabled for legacy docs: the current
                  correction-submit path (handleCorrectionSubmit) reads V2
                  PaymentOptions, which legacy results don't have. An existing
                  override still renders and can be reverted. */}
              <LegacyPatientResponsibility
                cr={legacyCollectionResult}
                onViewBenefitDetail={onViewFullBenefits}
                override={correctionOverride}
                onRevertCorrection={onRevertCorrection}
                canCorrect={false}
              />

              {legacyCollectionResult.resolvedBy === 'llm' && (
                <AiThoughtProcess reasoning={legacyCollectionResult.reasoning} />
              )}

              {(hasDeductible || hasOop) && (
                <DeductibleAccordion>
                  <div className="benefit-progress-cards">
                    {hasDeductible && (
                      <BenefitProgressCard
                        label="Deductible"
                        inTotal={summary.deductibleTotal}
                        inRemaining={summary.deductibleRemaining}
                        oonTotal={summary.deductibleOonTotal}
                        oonRemaining={summary.deductibleOonRemaining}
                      />
                    )}
                    {hasOop && (
                      <BenefitProgressCard
                        label="Out-of-Pocket Maximum"
                        inTotal={summary.oopTotal}
                        inRemaining={summary.oopRemaining}
                        oonTotal={summary.oopOonTotal}
                        oonRemaining={summary.oopOonRemaining}
                      />
                    )}
                  </div>
                </DeductibleAccordion>
              )}
            </>
          ) : (
            <>
              {summary.hasTiers && (
                <div className="med-tier-warning">
                  <div className="med-tier-warning__header">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    <span>PROVIDER TIER NOT VERIFIED</span>
                  </div>
                  <p className="med-tier-warning__text">
                    This plan has multiple network tiers. Verify the provider's tier to determine exact cost-sharing.
                  </p>
                </div>
              )}

              {summary.missingCostSharing && (
                <div className="slideout-panel__warning">
                  <p>Cost-sharing not specified from this payer</p>
                </div>
              )}

              {(hasCostSharing || hasDeductible || hasOop) && (
                <div className="financial-summary-section">
                  <div className="financial-summary-section__header">
                    <svg className="financial-summary-section__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" />
                      <path d="M12 18V6" />
                    </svg>
                    <span>FINANCIAL SUMMARY</span>
                  </div>

                  {hasCostSharing && useCompactTable && (
                    <div className="simple-value-cards">
                      {summary.copay && (
                        <div className="simple-value-card">
                          <div className="simple-value-card__title">Copay</div>
                          {summary.copay.inNetwork && (
                            <div className="simple-value-card__row">
                              <span className="simple-value-card__direction">IN-NETWORK</span>
                              <span className="simple-value-card__value">
                                {formatCurrency(singleVal(summary.copay.inNetwork))}
                                {summary.copay.inNetwork.planLevel && (
                                  <span className="slideout-table__plan-badge" title={PLAN_LEVEL_TOOLTIP}> Plan</span>
                                )}
                              </span>
                            </div>
                          )}
                          {summary.copay.outOfNetwork && singleVal(summary.copay.outOfNetwork) !== null && (
                            <div className="simple-value-card__row">
                              <span className="simple-value-card__direction">OUT-OF-NETWORK</span>
                              <span className="simple-value-card__value">{formatCurrency(singleVal(summary.copay.outOfNetwork))}</span>
                            </div>
                          )}
                        </div>
                      )}
                      {summary.coinsurance && (
                        <div className="simple-value-card">
                          <div className="simple-value-card__title">Coinsurance</div>
                          {summary.coinsurance.inNetwork && (
                            <div className="simple-value-card__row">
                              <span className="simple-value-card__direction">IN-NETWORK</span>
                              <span className="simple-value-card__value">
                                {singleVal(summary.coinsurance.inNetwork) !== null ? `${singleVal(summary.coinsurance.inNetwork)}%` : '-'}
                                {summary.coinsurance.inNetwork.planLevel && (
                                  <span className="slideout-table__plan-badge" title={PLAN_LEVEL_TOOLTIP}> Plan</span>
                                )}
                              </span>
                            </div>
                          )}
                          {summary.coinsurance.outOfNetwork && singleVal(summary.coinsurance.outOfNetwork) !== null && (
                            <div className="simple-value-card__row">
                              <span className="simple-value-card__direction">OUT-OF-NETWORK</span>
                              <span className="simple-value-card__value">{`${singleVal(summary.coinsurance.outOfNetwork)}%`}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {hasCostSharing && !useCompactTable && (
                    <div className="cost-sharing-cards">
                      {summary.copay && <CostSharingSection group={summary.copay} onViewSourceRows={onViewSourceRows} />}
                      {summary.coinsurance && <CostSharingSection group={summary.coinsurance} onViewSourceRows={onViewSourceRows} />}
                    </div>
                  )}

                  {(hasDeductible || hasOop) && (
                    <div className="benefit-progress-cards">
                      {hasDeductible && (
                        <BenefitProgressCard
                          label="Deductible"
                          inTotal={summary.deductibleTotal}
                          inRemaining={summary.deductibleRemaining}
                          oonTotal={summary.deductibleOonTotal}
                          oonRemaining={summary.deductibleOonRemaining}
                        />
                      )}
                      {hasOop && (
                        <BenefitProgressCard
                          label="Out-of-Pocket Maximum"
                          inTotal={summary.oopTotal}
                          inRemaining={summary.oopRemaining}
                          oonTotal={summary.oopOonTotal}
                          oonRemaining={summary.oopOonRemaining}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* SMS recovery cross-link — rejected checks with an embedded 271
              open THIS slideout (not the failure slideout), so the Recovery
              Loop link must live here too. */}
          <SmsRecoverySection
            inRecoveryLoop={inRecoveryLoop}
            smsRecoveryEligible={smsRecoveryEligible}
            onView={onViewRecoveryConversation}
          />
        </div>

        {/* Footer */}
        {closeWarned && editDirty && (
          <div className="slideout-unsaved" role="status">
            <span>{UNSAVED_WARNING}</span>
            <button className="slideout-unsaved__discard" onClick={onClose}>
              Discard
            </button>
          </div>
        )}
        <div className="slideout-panel__footer">
          {isEditing && !pendingMembers && (
            <button
              className="slideout-panel__rerun"
              disabled={!canRerun || rerunning}
              onClick={handleRerun}
            >
              {rerunning ? 'Checking...' : 'Rerun Eligibility Check'}
            </button>
          )}
          <button className="slideout-panel__cta" onClick={onViewFullBenefits}>
            View Full Benefits
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="m9 18 6-6-6-6" />
            </svg>
          </button>
        </div>
      </div>

      {jsonExportEnabled && (
        <RawJsonModal
          open={showRawJson}
          onClose={() => setShowRawJson(false)}
          data={jsonExport}
          filenameBase={`eligibility-${result.payer?.name || 'check'}-${checkId ?? 'manual'}`}
        />
      )}

      {showCorrectionToast && correctionOverride && (
        <div className="correction-toast">
          <strong>Override applied</strong>
          <span>
            Patient responsibility updated to {formatCorrectedValue(correctionOverride)} ({correctionOverride.selectedType === 'copay' ? 'Co-payment' : 'Co-insurance'})
          </span>
        </div>
      )}
    </>
  )
}
